import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { environment as ENV } from '@environment';
import { getAlpha2Code } from '@ux/mappers/place.mapper';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { TravelBookingType } from '@pax/entities/travel-booking-types';

export function isAnInternationalTripByBookingType(type: TravelBookingType, data: SearchData): boolean {
  switch (type) {
    case TravelBookingType.AUTOS:
      return isAnInternationalTripByAutos(data);
    case TravelBookingType.HOTELS:
      return isAnInternationalTripByHotels(data);
    case TravelBookingType.FLIGHTS:
    case TravelBookingType.FLIGHTS_BY_SECTOR:
      return isAnInternationalTripByFlights(data);
    case TravelBookingType.BUSES:
      return isAnInternationalTripByBuses(data);
    case TravelBookingType.PACKAGE_AIR:
      return isAnInternationalTripByPackages(data);
    default:
      return false;
  }
}

export function isAnInternationalTripBySearch(type: SearchBy, data: SearchData): boolean {
  switch (type) {
    case SearchBy.AUTOS:
      return isAnInternationalTripByAutos(data);
    case SearchBy.HOTELS:
    case SearchBy.BYHOURS:
      return isAnInternationalTripByHotels(data);
    case SearchBy.FLIGHTS:
      return isAnInternationalTripByFlights(data);
    case SearchBy.BUSES:
      return isAnInternationalTripByBuses(data);
    case SearchBy.PACKAGES:
      return isAnInternationalTripByPackages(data);
    case SearchBy.ACTIVITIES:
      return isInternationalTripByPlace(data?.departurePlace?.country);
    default:
      return false;
  }
}

export function isInternationalTripByPlace(codeCountry: string): boolean {
  if (codeCountry) {
    const isoPlace = getAlpha2Code(codeCountry);
    const isInternational = !!isoPlace && isoPlace !== ENV.countryInfo.iso_code;
    return isoPlace === null ? true : isInternational;
  } else return false;
}

export function isInternationalTripByCode(codeCountry: string): boolean {
  if (codeCountry) {
    const isInternational = !!codeCountry && codeCountry !== ENV.countryInfo.iso_code;
    return codeCountry === null ? true : isInternational;
  } else return false;
}

function isAnInternationalTripByHotels(data: SearchData): boolean {
  return isInternationalTripByPlace(data?.hotelDestinationPlace?.Country);
}

function isAnInternationalTripByAutos(data: SearchData): boolean {
  return isInternationalTripByPlaces(data.dropOffPlace, data.pickUpPlace);
}

function isAnInternationalTripByFlights(data: SearchData): boolean {
  return isInternationalTripByPlaces(data.arrivalPlace, data.departurePlace);
}

function isAnInternationalTripByBuses(data: SearchData): boolean {
  return isInternationalTripByPlaces(data.arrivalTerminal, data.departureTerminal);
}

function isAnInternationalTripByPackages(data: SearchData): boolean {
  return isInternationalTripByPlaces(data.destinationPlace, data.departurePlace);
}

function isInternationalTripByPlaces(arrivalPlace: AutocompletePlace, departurePlace: AutocompletePlace): boolean {
  const arrivalIsInternational = isInternationalTripByPlace(arrivalPlace?.country);
  const departureIsInternational = isInternationalTripByPlace(departurePlace?.country);
  return arrivalIsInternational || departureIsInternational;
}
